import React, {FC, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {useLinkNavigation} from '../../../../../../src/components/common/navigation/useLinkNavigation';
import {Trans} from '../../../../../shared/helpers/src/internationalization/react';
import {ChromelessButton} from '../../../../../shared/ui/src/components/buttons/chromelessButton';
import {Icon, IconName} from '../../../../../shared/ui/src/components/icon/icon';
import {blendColors} from '../../../../../shared/ui/src/styles/colorHelpers';
import {fontSizes, fontWeights} from '../../../../../shared/ui/src/styles/fontStyles';
import {KnowledgeBaseParametersTypesEnum} from '../../../../store/src/knowledgeBaseParameters';
import {buildListPath, buildSettingsPath} from '../../../../store/src/panel/knowledgeBasePanelRoutes';

/*
 * Types.
 */

interface NavItem {
  label: ReactNode;
  types: ReadonlyArray<KnowledgeBaseParametersTypesEnum>;
  path: () => string;
  iconName: IconName;
  selectedIconName: IconName;
}

/*
 * Constants.
 */

const navItems: ReadonlyArray<NavItem> = [
  {
    label: <Trans id="knowledge-base-navigation-content-label">Content</Trans>,
    types: [KnowledgeBaseParametersTypesEnum.LIST, KnowledgeBaseParametersTypesEnum.ARTICLE],
    path: buildListPath,
    iconName: 'knowledgeBaseSmall',
    selectedIconName: 'knowledgeBaseFilledSmall',
  },
  {
    label: <Trans id="knowledge-base-navigation-settings-label">Settings</Trans>,
    types: [KnowledgeBaseParametersTypesEnum.SETTINGS],
    path: buildSettingsPath,
    iconName: 'gearSmall',
    selectedIconName: 'gearFilledSmall',
  },
];

/*
 * Props.
 */

export interface KnowledgeBaseNavigationProps {
  currentParameterType: KnowledgeBaseParametersTypesEnum;
}

/*
 * Style.
 */

const StyledWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  border-right: 2px solid ${(p) => p.theme.greys.shade20};
  width: 240px;
  flex-shrink: 0;
`;

const StyledHeaderDiv = styled.div`
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.header};
  padding: 26px 8px 6px 14px;
`;

interface StyledNavigationItemProps {
  $isSelected: boolean;
}

const StyledNavigationItem = styled(ChromelessButton)<StyledNavigationItemProps>`
  display: flex;
  align-items: center;
  gap: 8px;

  height: 28px;
  padding: 3px 5px 3px 22px;
  margin: 0 5px;
  border-radius: 4px;

  color: ${(p) => (p.$isSelected ? p.theme.itemSelected : p.theme.item)};
  font-weight: ${fontWeights.normal};
  font-size: ${fontSizes.medium};

  background-color: ${(p) =>
    p.$isSelected ? blendColors(p.theme.background, p.theme.itemBackgroundSelected) : p.theme.background};

  ${(p) =>
    !p.$isSelected &&
    css`
      &:hover {
        background-color: ${blendColors(p.theme.background, p.theme.itemBackgroundHover)};
      }
    `}
`;

const StyledIcon = styled(Icon)`
  color: ${(p) => p.theme.icon};
`;

/*
 * Component.
 */

export const KnowledgeBaseNavigation: FC<KnowledgeBaseNavigationProps> = ({currentParameterType}) => {
  const {navigateToLink} = useLinkNavigation();
  return (
    <StyledWrapperDiv>
      <StyledHeaderDiv>
        <Trans id="knowledge-base-navigation-header">Knowledge Base</Trans>
      </StyledHeaderDiv>
      {navItems.map((navItem) => {
        const isSelected = navItem.types.includes(currentParameterType);
        const path = navItem.path();
        return (
          <StyledNavigationItem $isSelected={isSelected} key={path} onClick={() => navigateToLink(path)}>
            <StyledIcon name={isSelected ? navItem.selectedIconName : navItem.iconName} />
            <div>{navItem.label}</div>
          </StyledNavigationItem>
        );
      })}
    </StyledWrapperDiv>
  );
};
